import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Input, Spinner } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { ErrorLine, TabIntro, useAdminLoad } from '../aiAccess/shared';
import type { EnrichmentClient, ProductListItem, ProductLookup, ProductPage, ProductStats } from './api';
import { OEM_NAMES, ProductView, localTime, warrantyStatus } from './ProductView';

export const PRODUCTS_PAGE_SIZE = 25;
const SEARCH_DELAY_MS = 300;

/** How each check status reads on the stats tile. */
const CHECK_STATUSES: Record<string, string> = {
  success: 'Found',
  not_found: 'Not found',
  oem_unknown: 'OEM unknown',
  unsupported: 'Unsupported',
  needs_more_info: 'Needs info',
  error: 'Error',
};

/**
 * Stored products: record counts, then the tenant's serials (searchable, paged, newest first); a row
 * opens what is stored for it, and can ask the OEM again. `version` reloads both after a change.
 */
export function StoredProductsSection({ client, version, onStored }: {
  readonly client: EnrichmentClient | null;
  readonly version: number;
  readonly onStored: () => void;
}) {
  const { t } = useTranslation();
  const stats = useAdminLoad<ProductStats, EnrichmentClient>(client, (c, signal) => c.stats(signal));
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<ProductPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const requestRef = useRef<AbortController | null>(null);

  // The search goes out once typing pauses, from the first page.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim());
      setOffset(0);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const fetchPage = useCallback(async () => {
    if (!client) return;
    requestRef.current?.abort();
    const request = new AbortController();
    requestRef.current = request;
    setLoading(true);
    setError(null);
    try {
      const next = await client.products({ q: search, limit: PRODUCTS_PAGE_SIZE, offset }, request.signal);
      if (!request.signal.aborted) setPage(next);
    } catch (e) {
      if (!request.signal.aborted) setError((e as Error).message || t('Something went wrong.'));
    } finally {
      if (!request.signal.aborted) setLoading(false);
    }
  }, [client, search, offset, t]);

  const ready = Boolean(client);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  useEffect(() => {
    if (ready) void fetchRef.current();
  }, [ready, search, offset]);
  useEffect(() => () => requestRef.current?.abort(), []);

  // A lookup, bulk run or import changed the records: count and list them again.
  const reloadStatsRef = useRef(stats.reload);
  reloadStatsRef.current = stats.reload;
  const firstVersion = useRef(version);
  useEffect(() => {
    if (version === firstVersion.current) return;
    void reloadStatsRef.current();
    void fetchRef.current();
  }, [version]);

  const items = page?.items ?? [];
  const total = page?.total ?? 0;
  const refresh = () => {
    void stats.reload();
    void fetchPage();
  };

  return (
    <section aria-label={t('Stored products')} className="flex flex-col gap-3">
      <TabIntro
        title={t('Stored products')}
        actions={(
          <CtyButton variant="flat" size="sm" onPress={refresh} isDisabled={loading || !client}>
            {t('Refresh')}
          </CtyButton>
        )}
      >
        <span className="text-sm text-default-500">
          {t('The units, products and warranties stored for your tenant, from lookups and imports.')}
        </span>
        {stats.data ? (
          <span className="text-xs text-default-400">
            {t('Found answers are asked again after {{days}} days; serials the OEM did not know after {{retry}}.', {
              days: stats.data.recheck_days,
              retry: stats.data.not_found_retry_days,
            })}
          </span>
        ) : null}
      </TabIntro>

      <ErrorLine message={stats.error} />
      {stats.data && <StatsTiles stats={stats.data} />}

      <Input
        aria-label={t('Search stored products')}
        placeholder={t('Search by serial, product name, product number or model')}
        size="sm"
        className="max-w-md"
        value={query}
        onValueChange={setQuery}
        startContent={<Icon icon="lucide:search" width={16} />}
      />
      <ErrorLine message={error} />
      {page && items.length === 0 && !loading && (
        <p className="text-sm text-default-500">
          {search ? t('No stored products match your search.') : t('No products are stored yet.')}
        </p>
      )}
      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" aria-label={t('Stored products')}>
            <thead className="text-xs text-default-500">
              <tr>
                <th className="py-1 pr-4 font-medium">{t('Serial')}</th>
                <th className="py-1 pr-4 font-medium">{t('Product')}</th>
                <th className="py-1 pr-4 font-medium">{t('OEM')}</th>
                <th className="py-1 pr-4 font-medium">{t('Warranty')}</th>
                <th className="py-1 pr-4 font-medium">{t('Coverages')}</th>
                <th className="py-1 font-medium">{t('Last checked')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <Fragment key={item.serial_number}>
                  <ProductRow item={item} open={open === item.serial_number} onToggle={() => setOpen((current) => (current === item.serial_number ? null : item.serial_number))} />
                  {open === item.serial_number && (
                    <tr>
                      <td colSpan={6} className="pb-3">
                        <ProductDetail client={client} item={item} onStored={onStored} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {loading && <Spinner size="sm" className="self-start" />}
      {page && total > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-default-500">
          <span>
            {t('{{from}}–{{to}} of {{total}}', {
              from: offset + 1,
              to: offset + items.length,
              total: page.total_capped ? `${total}+` : total,
            })}
          </span>
          <CtyButton variant="flat" size="sm" onPress={() => setOffset(Math.max(0, offset - PRODUCTS_PAGE_SIZE))} isDisabled={loading || offset === 0}>
            {t('Previous')}
          </CtyButton>
          <CtyButton
            variant="flat"
            size="sm"
            onPress={() => setOffset(offset + PRODUCTS_PAGE_SIZE)}
            isDisabled={loading || (!page.total_capped && offset + items.length >= total) || items.length < PRODUCTS_PAGE_SIZE}
          >
            {t('Next')}
          </CtyButton>
        </div>
      )}
    </section>
  );
}

function StatsTiles({ stats }: { readonly stats: ProductStats }) {
  const { t } = useTranslation();
  const records = stats.records ?? {};
  const entitlements = stats.entitlements_by_status ?? {};
  const checks = Object.entries(stats.checks_by_status ?? {}).filter(([, count]) => count > 0);
  const tiles = [
    { key: 'serial', label: t('Serials'), value: records.serial ?? 0 },
    { key: 'product', label: t('Products'), value: records.product ?? 0 },
    { key: 'coverage', label: t('Coverages'), value: records.coverage ?? 0 },
    {
      key: 'entitlement',
      label: t('Entitlements'),
      value: records.entitlement ?? 0,
      note: t('{{active}} active · {{expired}} expired', {
        active: (entitlements.active ?? 0).toLocaleString(),
        expired: (entitlements.expired ?? 0).toLocaleString(),
      }),
    },
  ];
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map((tile) => (
          <div key={tile.key} role="group" aria-label={tile.label} className="flex flex-col gap-1 rounded-medium border border-default-200 p-3">
            <span className="text-xs text-default-500">{tile.label}</span>
            <span className="text-2xl font-semibold text-foreground">{tile.value.toLocaleString()}</span>
            {tile.note ? <span className="text-xs text-default-500">{tile.note}</span> : null}
          </div>
        ))}
      </div>
      {checks.length > 0 && (
        <div role="group" aria-label={t('Checks by outcome')} className="flex flex-wrap items-center gap-1 text-xs text-default-500">
          {t('Checks')}:
          {checks.map(([status, count]) => (
            <Chip key={status} size="sm" variant="flat">{t(CHECK_STATUSES[status] ?? status)} {count.toLocaleString()}</Chip>
          ))}
          <span>· {t('tenant {{tenant}}', { tenant: stats.tenant })}</span>
        </div>
      )}
    </div>
  );
}

function ProductRow({ item, open, onToggle }: {
  readonly item: ProductListItem;
  readonly open: boolean;
  readonly onToggle: () => void;
}) {
  const { t } = useTranslation();
  const status = item.warranty?.status ? warrantyStatus(item.warranty.status) : null;
  return (
    <tr className="border-t border-default-100 align-top">
      <td className="py-1 pr-4">
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className="flex items-center gap-1 font-mono text-xs text-primary"
        >
          <Icon icon={open ? 'lucide:chevron-down' : 'lucide:chevron-right'} width={14} />
          {item.serial_number}
        </button>
      </td>
      <td className="py-1 pr-4 text-xs">
        {item.product?.product_name || '—'}
        {item.product?.product_number ? <span className="block font-mono text-default-500">{item.product.product_number}</span> : null}
      </td>
      <td className="py-1 pr-4 text-xs">{item.oem?.name || OEM_NAMES[item.oem?.code ?? ''] || item.oem?.code || '—'}</td>
      <td className="py-1 pr-4 text-xs">
        {status ? <Chip size="sm" variant="flat" color={status.color}>{t(status.label)}</Chip> : '—'}
        {item.warranty?.end_date ? <span className="ml-2 whitespace-nowrap">{t('ends {{date}}', { date: item.warranty.end_date })}</span> : null}
      </td>
      <td className="py-1 pr-4 text-xs">{item.warranty?.coverage_count ?? 0}</td>
      <td className="whitespace-nowrap py-1 text-xs" title={item.check?.checked_at ?? undefined}>{localTime(item.check?.checked_at)}</td>
    </tr>
  );
}

/** What is stored for one serial; "Ask the OEM again" looks it up with refresh. */
function ProductDetail({ client, item, onStored }: {
  readonly client: EnrichmentClient | null;
  readonly item: ProductListItem;
  readonly onStored: () => void;
}) {
  const { t } = useTranslation();
  const serial = item.serial_number;
  const { data, setData, loading, error } = useAdminLoad<ProductLookup, EnrichmentClient>(client, (c, signal) => c.product(serial, signal));
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);

  const askAgain = async () => {
    if (!client) return;
    setAsking(true);
    setAskError(null);
    try {
      setData(await client.lookup({ serial_number: serial, oem: item.oem?.code ?? null, refresh: true }));
      onStored();
    } catch (e) {
      setAskError((e as Error).message || t('Something went wrong.'));
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 pt-1">
      {loading && <Spinner size="sm" className="self-start" />}
      <ErrorLine message={error} />
      {data && <ProductView answer={data} />}
      <div className="flex flex-wrap items-center gap-2">
        <CtyButton variant="flat" size="sm" onPress={() => void askAgain()} isDisabled={!client || asking} isLoading={asking}>
          {t('Ask the OEM again')}
        </CtyButton>
        {asking && <span className="text-xs text-default-500">{t('Asking the OEM; this takes about 20 seconds.')}</span>}
      </div>
      <ErrorLine message={askError} />
    </div>
  );
}
